/**
 * IFRAP-AI - Publications Page (Public)
 * Displays published content from IFRAP analysts
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, TrendingUp, Calendar, User, Tag, 
  ExternalLink, ChevronRight, Filter, Search,
  Presentation, BarChart3, Newspaper, AlertTriangle
} from 'lucide-react';
import api from '../../api/client';

const CONTENT_TYPE_CONFIG = {
  chronique: {
    icon: Newspaper,
    label: 'Chronique média',
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10',
  },
  rapport: {
    icon: FileText,
    label: 'Rapport d\'analyse',
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10',
  },
  revue_presse: {
    icon: Newspaper,
    label: 'Revue de presse',
    color: 'text-green-500',
    bgColor: 'bg-green-500/10',
  },
  infographie: {
    icon: BarChart3,
    label: 'Infographie',
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10',
  },
  presentation: {
    icon: Presentation,
    label: 'Présentation',
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10',
  },
};

const CATEGORIES = [
  { value: 'all', label: 'Toutes catégories' },
  { value: 'economie', label: 'Économie' },
  { value: 'finances_publiques', label: 'Finances publiques' },
  { value: 'energie', label: 'Énergie' },
  { value: 'social', label: 'Social' },
  { value: 'education', label: 'Éducation' },
  { value: 'sante', label: 'Santé' },
  { value: 'retraites', label: 'Retraites' },
  { value: 'collectivites', label: 'Collectivités' },
  { value: 'international', label: 'International' },
];

const PublicationCard = ({ publication }) => {
  const config = CONTENT_TYPE_CONFIG[publication.type] || CONTENT_TYPE_CONFIG.rapport;
  const Icon = config.icon;
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <article 
      className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:shadow-lg transition-all group"
      data-testid={`publication-card-${publication.id}`}
    >
      {/* Image header or colored header */}
      {publication.image_url ? (
        <div className="h-48 bg-gray-100 overflow-hidden">
          <img 
            src={publication.image_url} 
            alt={publication.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      ) : (
        <div className={`h-3 ${config.bgColor.replace('/10', '')}`} />
      )}
      
      {/* Content */}
      <div className="p-5">
        {/* Type badge */}
        <div className="flex items-center gap-2 mb-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.bgColor} ${config.color}`}>
            <Icon className="w-3.5 h-3.5" />
            {config.label}
          </span>
          {publication.tags?.includes('mocké') && (
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
              <AlertTriangle className="w-3 h-3" />
              Test IA
            </span>
          )}
        </div>
        
        {/* Title */}
        <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-[#000091] transition-colors">
          {publication.title}
        </h3>
        
        {/* Summary */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">
          {publication.summary}
        </p>
        
        {/* Meta info */}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Calendar className="w-3.5 h-3.5" />
              {formatDate(publication.published_at)}
            </span>
            {publication.views > 0 && (
              <span className="flex items-center gap-1">
                <TrendingUp className="w-3.5 h-3.5" />
                {publication.views} vues
              </span>
            )}
          </div>
          
          {publication.gamma_url && (
            <a
              href={publication.gamma_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-purple-600 hover:text-purple-700"
              onClick={(e) => e.stopPropagation()}
            >
              <Presentation className="w-3.5 h-3.5" />
              Gamma
            </a>
          )}
        </div>
        
        {/* Tags */}
        {publication.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-3 pt-3 border-t border-gray-100">
            {publication.tags.slice(0, 4).map((tag, idx) => (
              <span 
                key={idx}
                className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      
      {/* Read more link */}
      <div className="px-5 pb-4">
        <Link
          to={`/publications/${publication.id}`}
          className="inline-flex items-center gap-1 text-[#000091] font-medium text-sm hover:gap-2 transition-all"
        >
          Lire la suite
          <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
    </article>
  );
};

const PublicationsPage = () => {
  const [publications, setPublications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchPublications();
  }, [selectedCategory]);

  const fetchPublications = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const response = await api.get('/api/publications/public', { params });
      setPublications(response.data.publications || []);
    } catch (err) {
      console.error('Error fetching publications:', err);
      setError('Impossible de charger les publications');
    } finally {
      setLoading(false);
    }
  };

  const filteredPublications = publications.filter(pub => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      pub.title?.toLowerCase().includes(query) ||
      pub.summary?.toLowerCase().includes(query) ||
      pub.tags?.some(tag => tag.toLowerCase().includes(query))
    );
  });

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#000091] via-[#000091] to-[#1a1a6e] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-4">
              Publications IFRAP-AI
            </h1>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Analyses, rapports et chroniques produits par les analystes de la Fondation IFRAP
              avec l'assistance de l'intelligence artificielle.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="bg-white border-b border-gray-200 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
            {/* Search */}
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#000091] focus:border-transparent"
                data-testid="publications-search"
              />
            </div>
            
            {/* Category filter */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Filter className="w-4 h-4 text-gray-500" />
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="flex-1 sm:w-48 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#000091]"
                data-testid="publications-category-filter"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Publications grid */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-[#000091]/20 border-t-[#000091] rounded-full animate-spin"></div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-500">{error}</p>
            </div>
          ) : filteredPublications.length === 0 && !searchQuery && selectedCategory === 'all' ? (
            <div className="text-center py-16 bg-gray-50 rounded-xl border border-gray-200">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">
                Publications à venir
              </h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Les publications de nos analystes seront bientôt disponibles ici. 
                En attendant, retrouvez toutes nos études sur le site de l'IFRAP.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <a
                  href="https://www.ifrap.org/tous-les-articles"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#000091] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#000091]/90 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Voir les études IFRAP
                </a>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  Retour à l'observatoire
                </Link>
              </div>
            </div>
          ) : filteredPublications.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">Aucune publication trouvée</p>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="mt-2 text-[#000091] text-sm hover:underline"
                >
                  Effacer la recherche
                </button>
              )}
            </div>
          ) : (
            <>
              <p className="text-sm text-gray-500 mb-6">
                {filteredPublications.length} publication{filteredPublications.length > 1 ? 's' : ''}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredPublications.map((pub) => (
                  <PublicationCard key={pub.id} publication={pub} />
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-white border-t border-gray-200">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            Restez informé des nouvelles publications
          </h2>
          <p className="text-gray-600 mb-6">
            Activez les notifications pour être alerté dès qu'un nouveau rapport est publié.
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 bg-[#000091] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#000091]/90 transition-colors"
          >
            Retour à l'observatoire
            <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default PublicationsPage;
